import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Pencil, Trash2, Map as MapIcon } from "lucide-react";

const WRITE_ROLES = ["admin", "manager"];
const parseCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function TerritoriesPage() {
  const { role, activeTenantId } = useAuth();
  const canWrite = WRITE_ROLES.includes(role);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", cities: "", zip_codes: "", assigned_user_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/territories");
      setItems(res.data);
      if (canWrite) {
        const m = await api.get("/memberships").catch(() => ({ data: [] }));
        setMembers(m.data);
      }
    } finally {
      setLoading(false);
    }
  }, [canWrite]);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", cities: "", zip_codes: "", assigned_user_id: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, cities: (t.cities || []).join(", "),
      zip_codes: (t.zip_codes || []).join(", "), assigned_user_id: t.assigned_user_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: form.name, cities: parseCsv(form.cities), zip_codes: parseCsv(form.zip_codes),
      assigned_user_id: form.assigned_user_id || null,
    };
    try {
      if (editing) await api.put(`/territories/${editing.id}`, payload);
      else await api.post("/territories", payload);
      toast.success(editing ? "Territorio actualizado" : "Territorio creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/territories/${t.id}`);
      toast.success("Territorio eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const memberName = (uid) => members.find((m) => m.user_id === uid)?.name || (uid ? "—" : "Sin asignar");

  return (
    <div className="space-y-6" data-testid="territories-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A]">
          {role === "sales" ? "Territorios asignados a vos." : "Todos los territorios del tenant."}
        </p>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-territory-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md active:scale-[0.98] transition-colors">
                <Plus className="h-4 w-4 mr-1" /> Nuevo territorio
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="territory-dialog">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar territorio" : "Nuevo territorio"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Nombre</Label>
                  <Input data-testid="territory-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Ciudades (coma)</Label>
                  <Input data-testid="territory-cities-input" value={form.cities} onChange={(e) => setForm({ ...form, cities: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Códigos postales (coma)</Label>
                  <Input data-testid="territory-zips-input" value={form.zip_codes} onChange={(e) => setForm({ ...form, zip_codes: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Vendedor asignado</Label>
                  <Select value={form.assigned_user_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="territory-assignee-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-territory-button" onClick={save} disabled={!form.name} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <MapIcon className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A]">No hay territorios todavía.</p>
          </div>
        ) : (
          <Table data-testid="territories-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nombre</TableHead>
                <TableHead>Ciudades</TableHead>
                <TableHead>Códigos postales</TableHead>
                <TableHead>Asignado</TableHead>
                {canWrite && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`territory-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-[#71717A]">{(t.cities || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-[#71717A]">{(t.zip_codes || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-[#71717A]">{memberName(t.assigned_user_id)}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-territory-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-territory-${t.id}`} className="text-[#EF4444]"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
